import { Component, OnInit, OnDestroy, signal, inject, effect, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { 
  LucideAngularModule,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X,
  Clock,
  MapPin,
  BellRing,
  Bell
} from 'lucide-angular';
import { PositionAlertService } from '../../../../core/services/position-alert.service';
import { PatrolMonitorService, PatrolAlert } from '../../../../core/services/patrol-monitor.service';
import { Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';

@Component({
  selector: 'app-position-alert-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: `./reminders-modal.component.html`
})
export class PositionAlertModalComponent implements OnInit, OnDestroy {
  private positionAlertService = inject(PositionAlertService);
  private patrolService = inject(PatrolMonitorService);
  private platformId = inject(PLATFORM_ID);
  private subscription = new Subscription();

  // Icons
  readonly AlertTriangleIcon = AlertTriangle;
  readonly AlertCircleIcon = AlertCircle;
  readonly CheckCircleIcon = CheckCircle;
  readonly XIcon = X;
  readonly ClockIcon = Clock;
  readonly MapPinIcon = MapPin;
  readonly BellRingIcon = BellRing;
  readonly BellIcon = Bell;

  private autoCloseTimer: any = null;
  private remainingTime = signal<number>(15); 
  private timerInterval: any = null;
  private isBrowser = isPlatformBrowser(this.platformId);

  // Indica que o modal está em fila
  private isInQueue = signal<boolean>(false);
  
  // Signals
  isOpen = this.positionAlertService.isOpen;
  currentAlert = this.positionAlertService.currentAlert;

  // Número de alertas na fila
  queueCount = signal<number>(0);

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.isOpen()) {
      this.closeModal();
    }
  }

  ngOnInit(): void {
    // Abre automaticamente quando o service dispara
    this.subscription.add(
      this.positionAlertService.openModal$.subscribe(alert => {
        if (alert && !('action' in alert)) {
          console.log('Modal abriu para o posto:', alert.position_name);
          this.isInQueue.set(true);
          this.resetAutoCloseTimer();
          this.updateQueueCount();
        }
      })
    );

    effect(() => {
      if (this.isOpen()) {
        // Modal abriu - inicia o timer
        this.startAutoCloseTimer();
        this.isInQueue.set(false);
        this.updateQueueCount();
      } else {
        // Modal fechou - limpa o timer
        this.clearAutoCloseTimer();
        // Verifica se há mais alertas na fila
        setTimeout(() => {
          this.updateQueueCount();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearAutoCloseTimer();
  }

  private updateQueueCount(): void {
    try {
      const alert = this.currentAlert();
      if (alert) {
        // this.patrolService.playAlertSound()

      }
    } catch (error) {
      console.debug('Erro ao atualizar contagem da fila:', error);
    }
  }

  private startAutoCloseTimer(): void {
    // Limpar timers
    this.clearAutoCloseTimer();

    // Iniciar tempo
    this.remainingTime.set(15);

    this.timerInterval = setInterval(() => {
      const currentTime = this.remainingTime();
      if (currentTime > 0) {
        this.remainingTime.set(currentTime - 1);
      } else {
        this.closeModal();
      }
    }, 1000);

    // Timer para fechar
    this.autoCloseTimer = setTimeout(() => {
      if (this.isOpen()) {
        this.closeModal();
      }
    }, 15000);
  }

  private resetAutoCloseTimer(): void {
    this.clearAutoCloseTimer();
    if (this.isOpen()) {
      this.startAutoCloseTimer();
    }
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  getRemainingTime(): number {
    return this.remainingTime();
  }

  getFormattedRemainingTime(): string {
    return `${this.remainingTime()}s`;
  }

  /**
   * Fecha o modal
   */
  closeModal(): void {
    this.clearAutoCloseTimer();
    this.positionAlertService.closeModal();
  }

  /**
   * Resolve o alerta atual
   */
  resolveAlert(): void {
    const alert = this.currentAlert();
    if (alert) {
      this.patrolService.resolveAlert(alert.id);
      this.clearAutoCloseTimer();
      this.positionAlertService.closeModal();
    }
  }

  /**
   * Retorna o ícone do alerta
   */
  getIcon(type?: string): any {
    const icons: Record<string, any> = {
      'no_movement': this.AlertTriangleIcon,
      'patrol_delay': this.AlertCircleIcon,
      'patrol_missing': this.AlertTriangleIcon,
    };
    return icons[type || ''] || this.AlertTriangleIcon;
  }

  /**
   * Retorna a classe do header
   */
  getHeaderClass(type?: string): string {
    const classes: Record<string, string> = {
      'no_movement': 'bg-gradient-to-r from-red-600 to-red-700',
      'patrol_delay': 'bg-gradient-to-r from-orange-500 to-orange-600',
      'patrol_missing': 'bg-gradient-to-r from-red-600 to-red-700',
    };
    return classes[type || ''] || 'bg-gradient-to-r from-gray-600 to-gray-700';
  }

  /**
   * Retorna a classe do status
   */
  getStatusClass(status?: string): string {
    const classes: Record<string, string> = {
      'active': 'text-red-600',
      'resolved': 'text-green-600',
      'ignored': 'text-gray-400',
    };
    return classes[status || ''] || 'text-gray-600';
  }

  /**
   * Retorna a cor do status dot
   */
  getStatusDot(status?: string): string {
    const classes: Record<string, string> = {
      'active': 'bg-red-500',
      'resolved': 'bg-green-500',
      'ignored': 'bg-gray-400',
    };
    return classes[status || ''] || 'bg-gray-400';
  }

  /**
   * Retorna o label do status
   */
  getStatusLabel(status?: string): string {
    const labels: Record<string, string> = {
      'active': 'Activo',
      'resolved': 'Resolvido',
      'ignored': 'Ignorado',
    };
    return labels[status || ''] || status || 'Desconhecido';
  }

  /**
   * Formata data
   */
  formatDate(date: any): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-MZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Formata data e hora
   */
  formatDateTime(date: any): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('pt-MZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}